import type { BillingPlanOption, BillingStatusResponse } from '@adop/shared';
import { BILLING_PLAN_IDS } from '@adop/shared';
import { planInfo } from '@adop/shared/plans';

/**
 * Pure billing presentation logic. Kept out of the page component so it is
 * unit-testable and so the page file only exports a component (fast refresh).
 */

/** Badge colour for a Stripe subscription status. */
export function planStatusVariant(
  status: string,
): 'success' | 'warning' | 'danger' | 'default' {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'success';
    case 'past_due':
    case 'incomplete':
      return 'warning';
    case 'canceled':
      return 'danger';
    default:
      return 'default';
  }
}

/**
 * Why a plan action is unavailable, or null when it can be taken. The reason is
 * always shown next to the disabled button so nothing is silently dead.
 */
export function planActionReason({
  configured,
  canOwn,
  plan,
}: {
  configured: boolean;
  canOwn: boolean;
  plan: BillingPlanOption;
}): string | null {
  if (!configured) return 'Billing is not configured yet.';
  if (!plan.configured) return `No Stripe price is configured for the ${plan.label} plan.`;
  if (!canOwn) return 'Only the owner can change the plan.';
  return null;
}

/** Plans to show, falling back to the shared catalog if the API sent none. */
export function planOptions(status: BillingStatusResponse): BillingPlanOption[] {
  if (status.plans.length > 0) return status.plans;
  return BILLING_PLAN_IDS.map((id) => ({
    id,
    label: planInfo(id)?.label ?? id,
    configured: false,
  }));
}
